import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Box, Card, CardContent, Divider, CardHeader } from '@mui/material';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';

function InventoryMonitoring() {
    const [grnData, setGrnData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [username, setUsername] = useState('');
    const [itemName, setItemName] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    useEffect(() => {
        fetchGrnData();
    }, []);

    useEffect(() => {
        filterGrnData();
    }, [grnData, fromDate, toDate, itemName]);

    const fetchGrnData = async () => {
        try {
            const res = await axios.get('https://serveremilia.mylksystems.com/grn/GetAllGrns');
            if (res.data.length > 0) {
                // Sort data by date in descending order (latest date first)
                const sortedData = res.data.sort((a, b) => new Date(b.creation_date) - new Date(a.creation_date));
                setGrnData(sortedData);
                setFilteredData(sortedData); // Initially, show all sorted data
            } else {
                toast.error("No GRN records found!");
            }
        } catch (error) {
            console.error("Error fetching GRN data", error);
        }
    };

    const filterGrnData = () => {
        let filtered = grnData;

        // Filter by itemName
        if (itemName) {
            filtered = filtered.filter(grn => grn.itemName.toLowerCase().includes(itemName.toLowerCase()));
        }

        // Filter by date range (including equal dates)
        if (fromDate) {
            filtered = filtered.filter(grn => new Date(grn.creation_date) >= new Date(fromDate));
        }

        if (toDate) {
            filtered = filtered.filter(grn => new Date(grn.creation_date) <= new Date(toDate));
        }

        setFilteredData(filtered);
    };

    const downloadCSV = (fileName, data) => {
        let csvContent = "Item Name,Supplier Name,Date,Total Amount,Qty,Unit Price\n";
        data.forEach(grn => {
            const unitPrice = parseFloat(grn.totalAmount) / parseFloat(grn.qty);
            csvContent += `${grn.itemName},${grn.supplierName},${grn.creation_date},${grn.totalAmount},${grn.qty},${unitPrice.toFixed(2)}\n`;
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, fileName);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Card sx={{ padding: '15px' }}>
            <CardContent>
                <CardHeader title="Inventory Monitoring" />
                <Divider />
                <Box display="flex" gap={2} mt={2} mb={2}>
                    <TextField
                        label="Search Item Name"
                        variant="outlined"
                        size="small"
                        value={itemName}
                        onChange={(e) => setItemName(e.target.value)}
                    />
                    <TextField
                        label="From Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                    />
                    <TextField
                        label="To Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                    />
                    <Button variant="contained" color="primary" onClick={() => downloadCSV("inventory_report.csv", filteredData)}>
                        Download CSV
                    </Button>
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Supplier Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Amount (Rs.)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Unit Price (Rs.)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                                const unitPrice = parseFloat(row.totalAmount) / parseFloat(row.qty);
                                const formattedDate = new Date(row.creation_date).toISOString().split('T')[0]; // Format as YYYY-MM-DD
                                return (
                                    <TableRow key={index}>
                                        <TableCell>{row.itemName}</TableCell>
                                        <TableCell>{row.supplierName}</TableCell>
                                        <TableCell>{formattedDate}</TableCell>
                                        <TableCell>{parseFloat(row.totalAmount).toFixed(2)}</TableCell>
                                        <TableCell>{row.qty}</TableCell>
                                        <TableCell>{unitPrice.toFixed(2)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </CardContent>
        </Card>
    );
}

export default InventoryMonitoring;
